import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useAuth } from '@clerk/clerk-react';
import { setClerkTokenGetter, onboardingApi } from './services/api';
import { Layout } from './components/Layout';
import { ToastProvider } from './components/Toast';
import { Dashboard } from './pages/Dashboard';
import { Orders } from './pages/Orders';
import { Products } from './pages/Products';
import { Settings } from './pages/Settings';
import { PackingStation } from './pages/PackingStation';
import { ShippingStation } from './pages/ShippingStation';
import { Shipments } from './pages/Shipments';
import { Employees } from './pages/Employees';
import { AutomationRules } from './pages/AutomationRules';
import { Customers } from './pages/Customers';
import { BlockedAddresses } from './pages/BlockedAddresses';
import { RatesEstimator } from './pages/RatesEstimator';
import { Reports } from './pages/Reports';
import { TimeReport } from './pages/TimeReport';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';
import { WorkstationLockScreen } from './components/WorkstationLockScreen';
import { InactivityWarning } from './components/InactivityWarning';
import { ProtectedRoute } from './components/ProtectedRoute';
import * as printerService from './services/printer';
function App() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [isRegistered, setIsRegistered] = useState<boolean | null>(null);
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  // Setup Clerk token getter for API requests
  useEffect(() => {
    if (isLoaded) {
      setClerkTokenGetter(async () => {
        if (isSignedIn) {
          try {
            const token = await getToken();
            if (!token) {
              console.warn('[Clerk] getToken returned null despite isSignedIn=true');
            }
            return token;
          } catch (error) {
            console.error('[Clerk] Error getting token:', error);
            return null;
          }
        }
        console.warn('[Clerk] Not signed in, cannot get token');
        return null;
      });
    }
  }, [isLoaded, isSignedIn, getToken]);

  // Clear employee storage when user signs out
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      // User signed out - clear any employee session data
      sessionStorage.removeItem('active_employee_session');
      localStorage.removeItem('logged_in_employee');
      console.log('[App] Cleared employee storage on sign-out');
    }
  }, [isLoaded, isSignedIn]);

  // Auto-connect to QZ Tray on app load if printer configured
  useEffect(() => {
    const autoConnectPrinter = async () => {
      const settings = printerService.getPrinterSettings();
      if (settings.labelPrinter) {
        console.log('[App] Auto-connecting to QZ Tray...');
        try {
          await printerService.connectToQz();
          console.log('[App] QZ Tray connected successfully');
        } catch (error: any) {
          console.warn('[App] QZ Tray auto-connect failed:', error.message);
          // Silent fail - user can manually connect in Settings
        }
      }
    };
    
    // Delay slightly to let app fully initialize
    const timer = setTimeout(autoConnectPrinter, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Check if user is registered when they sign in
  useEffect(() => {
    const checkAndRegisterUser = async () => {
      if (!isLoaded || !isSignedIn) {
        setIsRegistered(null);
        return;
      }

      try {
        // First, check if user is registered
        const checkResult = await onboardingApi.check();
        if (checkResult.registered) {
          setIsRegistered(true);
          return;
        }
      } catch (error: any) {
        // 404 means not registered - that's expected for new users
        if (error.response?.status !== 404) {
          console.error('Error checking registration:', error);
        }
      }

      // User not registered - auto-register them
      try {
        console.log('New user detected, auto-registering...');
        
        // Clear any stale employee data from previous accounts
        sessionStorage.removeItem('active_employee_session');
        localStorage.removeItem('logged_in_employee');
        
        // Check for invite token in URL (e.g. /sign-up?invite=abc123)
        const params = new URLSearchParams(window.location.search);
        const inviteToken = params.get('invite') || undefined;
        if (inviteToken) {
          console.log('[App] Found invite token, linking to invited org...');
        }
        
        await onboardingApi.register(undefined, inviteToken);
        setIsRegistered(true);
        console.log('User registered successfully');
      } catch (regError: any) {
        console.error('Registration failed:', regError);
        setRegistrationError(regError.response?.data?.error || 'Failed to create account');
        setIsRegistered(false);
      }
    };

    checkAndRegisterUser();
  }, [isLoaded, isSignedIn]);

  // Show loading while Clerk initializes
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4">🚢</div>
          <p className="text-slate-600">Loading CraftyShip...</p>
        </div>
      </div>
    );
  }

  // Admin routes (no Clerk auth required)
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/*" element={<Navigate to="/admin/login" replace />} />
      </Routes>
    );
  }

  // Show sign-in if not authenticated
  if (!isSignedIn) {
    return (
      <Routes>
        <Route path="/sign-in/*" element={
          <div className="flex items-center justify-center h-screen bg-slate-50">
            <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />
          </div>
        } />
        <Route path="/sign-up/*" element={
          <div className="flex items-center justify-center h-screen bg-slate-50">
            <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
          </div>
        } />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  // Show loading while checking/completing registration
  if (isRegistered === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4">🚢</div>
          <p className="text-slate-600">Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Show error if registration failed
  if (registrationError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Account Setup Failed</h2>
          <p className="text-slate-600 mb-4">{registrationError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // User is authenticated and registered - show main app
  return (
    <ToastProvider>
      {/* Main app content - each route is protected by permission check */}
      <Routes>
        <Route path="/" element={
          <Layout title="Dashboard">
            <ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/orders" element={
          <Layout title="Orders">
            <ProtectedRoute permission="orders"><Orders /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/packing" element={
          <Layout title="Packing Station">
            <ProtectedRoute permission="packing"><PackingStation /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/shipping" element={
          <Layout title="Shipping Station">
            <ProtectedRoute permission="shipping"><ShippingStation /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/rates-estimator" element={
          <Layout title="Rates Estimator">
            <ProtectedRoute permission="shipping"><RatesEstimator /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/products" element={
          <Layout title="Products">
            <ProtectedRoute permission="products"><Products /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/shipments" element={
          <Layout title="Shipments">
            <ProtectedRoute permission="shipments"><Shipments /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/employees" element={
          <Layout title="Employees">
            <ProtectedRoute permission="employees"><Employees /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/automation" element={
          <Layout title="Automation Rules">
            <ProtectedRoute permission="automation"><AutomationRules /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/customers" element={
          <Layout title="Customers">
            <ProtectedRoute permission="customers"><Customers /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/blocked-addresses" element={
          <Layout title="Blocked Addresses">
            <ProtectedRoute permission="blocked-addresses"><BlockedAddresses /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/reports" element={
          <Layout title="Reports">
            <ProtectedRoute permission="reports"><Reports /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/time-report" element={
          <Layout title="Time Report">
            <ProtectedRoute permission="time-report"><TimeReport /></ProtectedRoute>
          </Layout>
        } />
        <Route path="/settings" element={
          <Layout title="Settings">
            <ProtectedRoute permission="settings"><Settings /></ProtectedRoute>
          </Layout>
        } />
        {/* Catch-all: redirect any unknown routes (including SSO callbacks) to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {/* Lock screen overlay - rendered after Routes so it doesn't interfere with navigation */}
      <WorkstationLockScreen />
      
      {/* Inactivity warning toast */}
      <InactivityWarning />
    </ToastProvider>
  );
}

export default App;
